import * as React from "react"
import { FaEnvelope, FaPhoneAlt, FaUser } from "react-icons/fa"
import { LEAD_ENDPOINT, LEAD_LIMITS } from "../../../lib/lead-endpoint"

/**
 * The quote form — MTC-FCS-017, the whole of the Lead Generation tier.
 * ============================================================================
 * Every design gets THIS form. The Mockup Maker drew its forms as `<div>`s
 * because a mockup is a picture of a website; here the contractor is paying
 * $24.98 a month for enquiries to arrive, so the fields are real, the button
 * submits, and it posts to an endpoint that exists — see lib/lead-endpoint.js
 * for why that address is absolute.
 *
 * ── ONE DEFINITION, TWO SETS OF CLOTHES ────────────────────────────────────
 * What a lead IS — the field names, what is required, the length caps, where
 * it goes — is settled once, here, so five designs cannot drift into five
 * different ideas of an enquiry. Only the class names change:
 *
 *   variant "v2"  the icon-in-a-rounded-box card the v2 hero was drawn with
 *   default       `mk-quote`, styled in styles/templates/port.css from the
 *                 same palette tokens every design already sets, so it lands
 *                 in each one wearing that design's own colours
 *
 * ── THE PAGE SHIPS NO JAVASCRIPT ───────────────────────────────────────────
 * So this is a plain POST and the server answers with a page. It works with
 * scripting switched off, which is the point of a site that is one file.
 */

/** The services this contractor offers, for the "what do you need" list. */
const serviceOptions = view => {
  const named = view.services.map(s => s.title).filter(Boolean)
  return named.length > 0 ? named : [view.trade]
}

const QuoteForm = ({ view, variant }) => {
  const options = serviceOptions(view)

  /* The hidden `site` is which published website the enquiry belongs to. The
     endpoint refuses a slug it cannot find, so this is not a field anyone can
     usefully tamper with — it is how the lead reaches the right contractor. */
  const site = <input type="hidden" name="site" value={view.siteSlug} />

  if (variant === "v2") {
    return (
      <form className="v2-form" method="post" action={LEAD_ENDPOINT}>
        {site}
        <div className="v2-form-group">
          <FaUser />
          <input
            className="v2-input"
            name="name"
            placeholder="Full Name"
            aria-label="Full name"
            autoComplete="name"
            maxLength={LEAD_LIMITS.name}
            required
          />
        </div>
        <div className="v2-form-group">
          <FaPhoneAlt />
          <input
            className="v2-input"
            name="phone"
            type="tel"
            placeholder="Phone Number"
            aria-label="Phone number"
            autoComplete="tel"
            maxLength={LEAD_LIMITS.phone}
          />
        </div>
        <div className="v2-form-group">
          <FaEnvelope />
          <input
            className="v2-input"
            name="email"
            type="email"
            placeholder="Email Address"
            aria-label="Email address"
            autoComplete="email"
            maxLength={LEAD_LIMITS.email}
          />
        </div>
        <div className="v2-form-group">
          {/* The chevron the design draws is the select's own arrow here, so
              the native one is turned off in port.css rather than sitting
              underneath a second painted one. */}
          <select className="v2-input is-plain" name="service" aria-label="What do you need">
            {options.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="v2-form-group">
          <textarea
            className="v2-input is-area"
            name="message"
            placeholder="Project Details (optional)"
            aria-label="Project details"
            maxLength={LEAD_LIMITS.message}
          />
        </div>
        <button type="submit" className="v2-submit">
          Submit Request
        </button>
      </form>
    )
  }

  return (
    <form className="mk-quote" method="post" action={LEAD_ENDPOINT}>
      {site}
      <label className="mk-quote-field">
        <span>Full name</span>
        <input name="name" autoComplete="name" maxLength={LEAD_LIMITS.name} required />
      </label>
      <label className="mk-quote-field">
        <span>Phone</span>
        <input name="phone" type="tel" autoComplete="tel" maxLength={LEAD_LIMITS.phone} />
      </label>
      <label className="mk-quote-field">
        <span>Email</span>
        <input name="email" type="email" autoComplete="email" maxLength={LEAD_LIMITS.email} />
      </label>
      <label className="mk-quote-field">
        <span>What do you need?</span>
        <select name="service">
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      <label className="mk-quote-field mk-quote-wide">
        <span>Project details</span>
        <textarea name="message" rows={4} maxLength={LEAD_LIMITS.message} />
      </label>
      <button type="submit" className="mk-quote-submit">
        Send my request
      </button>
      <p className="mk-quote-foot">
        {/* Says only what is true: the enquiry is stored and the contractor
            answers it. Nothing here claims an email has gone anywhere. */}
        Your details go straight to {view.name}.
      </p>
    </form>
  )
}

export default QuoteForm
